import React from 'react';
import { shape, string, arrayOf } from 'prop-types';
import classnames from 'classnames';
import { Pill } from '@andes/badge';
import colornames from '../../lib/colornames';
import IconFactory from '../icons/factory-builder';
import Tooltip from '../tooltip';
import labelToProps from '../styled-label/label-to-props';
import StyledLabel from '../styled-label';
import withMappedProps from '../../utils/withMappedProps';
import { FeatureFeedback } from '../feature-feedback';
import { trackEvent } from '../../lib/tracking';
import { useLocalStorage } from '../../hooks/use-local-storage';
import { serializeLabel } from '../../utils/label-utils';
import { validateSnackbarColor } from '../../utils/validators';

const LabelComponent = withMappedProps(StyledLabel)(labelToProps);

const namespace = 'ui-pdp-vehicle-verification-report';

const buildFeedbackComponent = props => <FeatureFeedback {...props} />;

const VehicleVerificationReport = ({ header, badge, attribute, report, feedback_component: feedbackProps }) => {
  const [storedFeedback] = useLocalStorage(feedbackProps.local_storage_key || '');
  return (
    <div className={classnames(namespace)}>
      <StyledLabel as="h2" className={`${namespace}__header`} {...header}>
        {badge && (
          <Pill
            className={`${namespace}__pill`}
            color={validateSnackbarColor(badge.type?.toLowerCase())}
            size={badge.font_size?.toLowerCase()}
            hierarchy={badge.hierarchy?.toLowerCase()}
            roundedCorners={['top-right', 'top-left', 'bottom-right', 'bottom-left']}
            data-testid="vvr-badge"
          >
            {badge.text}
          </Pill>
        )}
      </StyledLabel>
      <div className={`${namespace}__specs-list`}>
        <>
          <div key={serializeLabel(attribute.title.text)} className={`${namespace}__spec`} style={{ width: 'auto' }}>
            <div aria-hidden="true" className={`${namespace}__icon ${namespace}__filled-icon--ROUNDED`}>
              {IconFactory('file', classnames(colornames('BLACK')))}
            </div>
            <div>
              <LabelComponent className={`${namespace}__spec_text`} label={attribute.title} />
              <LabelComponent className={`${namespace}__spec_text`} label={attribute.description} />
            </div>
          </div>
        </>
      </div>
      <div className={`${namespace}__download-link ${namespace}__desktop`}>
        {!storedFeedback ? (
          <Tooltip
            className={`${namespace}__tooltip`}
            title=""
            side="bottomLeft"
            content={buildFeedbackComponent(feedbackProps)}
            offsetY={8}
            trigger="hover"
          >
            <a href={report.url} onClick={() => trackEvent(report.track)}>
              {report.text}
            </a>
          </Tooltip>
        ) : (
          <a href={report.url} onClick={() => trackEvent(report.track)}>
            {report.text}
          </a>
        )}
      </div>
      <div className={`${namespace}__mobile`}>
        <div className={`${namespace}__download-link`}>
          <a href={report.url} onClick={() => trackEvent(report.track)}>
            {report.text}
          </a>
        </div>
        {feedbackProps && buildFeedbackComponent(feedbackProps)}
      </div>
    </div>
  );
};

VehicleVerificationReport.propTypes = {
  header: shape({
    text: string,
  }).isRequired,
  badge: shape({
    text: string,
    type: string,
    font_size: string,
    hierarchy: string,
    border: string,
  }),
  attribute: shape({
    title: shape({
      text: string,
      font_size: string,
      font_family: string,
    }),
    description: shape({
      text: string,
      font_size: string,
    }),
    icon: shape({
      id: string,
      color: string,
    }),
  }).isRequired,
  report: shape({
    text: string,
    url: string,
  }).isRequired,
  feedback_component: shape({
    title: shape({
      text: string,
    }),
    local_storage_key: string.isRequired,
    actions: arrayOf(
      shape({
        text: string,
        icon: string,
        id: string,
        congrats: shape({
          text: string,
        }),
      }),
    ),
  }).isRequired,
};

VehicleVerificationReport.defaultProps = {
  badge: null,
};

VehicleVerificationReport.displayName = 'VehicleVerificationReport';

export default React.memo(VehicleVerificationReport);
